import { AreaEstado } from '../services/dashboardService';

export type EstadoVisible = 'rojo' | 'amarillo' | 'verde' | 'pausada';

// El backend usa Verde/Amarillo/Rojo y "Desactivada" (pausa manual o fuera de horario)
export const estadoVisible = (area: Pick<AreaEstado, 'estado' | 'desactivadaManualmente'>): EstadoVisible => {
  if (area.desactivadaManualmente) return 'pausada';
  switch ((area.estado || '').toLowerCase()) {
    case 'rojo': return 'rojo';
    case 'amarillo': return 'amarillo';
    case 'verde': return 'verde';
    default: return 'pausada';
  }
};

// Etiqueta y colores por estado. La etiqueta siempre acompaña al color.
export const ESTADO_INFO: Record<EstadoVisible, { etiqueta: string; punto: string; chip: string; texto: string }> = {
  rojo: { etiqueta: 'Urgente', punto: 'bg-red-500', chip: 'bg-red-50 border-red-300 text-red-800', texto: 'font-semibold text-red-700' },
  amarillo: { etiqueta: 'Atención', punto: 'bg-yellow-400', chip: 'bg-yellow-50 border-yellow-300 text-yellow-800', texto: 'font-semibold text-yellow-700' },
  verde: { etiqueta: 'Al día', punto: 'bg-green-500', chip: 'bg-green-50 border-green-200 text-green-800', texto: 'text-gray-500' },
  pausada: { etiqueta: 'En pausa', punto: 'bg-gray-400', chip: 'bg-gray-50 border-gray-200 text-gray-500', texto: 'text-gray-500' },
};

export const ORDEN_ESTADO: Record<EstadoVisible, number> = { rojo: 0, amarillo: 1, verde: 2, pausada: 3 };

export const formatMinutos = (m: number): string => {
  if (m < 1) return 'menos de 1 min';
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  if (h >= 48) return `${Math.floor(h / 24)} días`;
  return mm === 0 ? `${h} h` : `${h} h ${mm} min`;
};

export const minutosDesde = (iso?: string | null, ahora: number = Date.now()): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, (ahora - t) / 60000);
};

// Sin limpiezas en más días que esto, el área probablemente salió de la rutina:
// se muestra aparte para que no tape a las que sí se limpian a diario
export const DIAS_OLVIDADA = 7;

export const estaOlvidada = (area: Pick<AreaEstado, 'ultimaLimpieza'>, ahora: number = Date.now()): boolean => {
  const desde = minutosDesde(area.ultimaLimpieza, ahora);
  return desde == null || desde >= DIAS_OLVIDADA * 24 * 60;
};

// Los nombres suelen terminar en el piso ("Baño Público Piso 2"); junto al piso sobra
export const nombreSinPiso = (nombre: string, pisoNombre: string): string => {
  const numeroPiso = pisoNombre.match(/-?\d+/)?.[0];
  const m = nombre.match(/^(.*?)[\s,·-]*\bpiso\s*(-?\d+)\s*$/i);
  if (!numeroPiso || !m || m[2] !== numeroPiso || !m[1].trim()) return nombre;
  return m[1].trim();
};

// Texto corto que explica por qué un área necesita atención
export const detalleEstado = (area: AreaEstado, ahora: number = Date.now()): string => {
  const estado = estadoVisible(area);
  if (estado === 'amarillo' && area.minutosRestantesRojo != null) {
    return `Pasa a urgente en ${formatMinutos(area.minutosRestantesRojo)}`;
  }
  const desde = minutosDesde(area.ultimaLimpieza, ahora);
  if (desde == null) return 'Sin limpiezas registradas';
  return `Última limpieza hace ${formatMinutos(desde)}`;
};
